import { useState, createContext, ReactNode, useSyncExternalStore, useCallback, useRef } from 'react'
import * as _ from 'lodash-es'
import { useStorage } from '../hooksF/useStorageF.tsx'

type StorageOptions = {
  reviver?: (this: any, key: string, value: any) => any
  replacer?: (this: any, key: string, value: any) => any
}

const noopStorage = { getItem: () => null, setItem: () => {}, removeItem: () => {} } as unknown as Storage

export const useLocalStorage = (key: string | string[], initialVal?: any, options?: StorageOptions) => {
  const storage = typeof localStorage === 'undefined' ? noopStorage : localStorage
  return useStorage(storage, key, initialVal, options)
}

const syncCache = new Map<string, any>()
const syncSubs = new Map<string, Set<() => void>>()

const syncRead = (storage: Storage, key: string, initialVal: any, options?: StorageOptions) => {
  try {
    const encoded = storage.getItem(key)
    if (encoded === null || encoded === 'undefined') return initialVal
    const state = JSON.parse(encoded, options?.reviver)
    if (_.has(state, '_fixSerializeOfFalsies')) return state._fixSerializeOfFalsies
    return state
  } catch {
    storage.removeItem(key)
    return initialVal
  }
}

const syncNotify = (key: string) => {
  syncCache.delete(key)
  syncSubs.get(key)?.forEach((cb) => cb())
}

export const useLocalStorageSync = (key: string, initialVal?: any, options?: StorageOptions) => {
  const storage = typeof localStorage === 'undefined' ? noopStorage : localStorage

  const stableInitialRef = useRef<{set: boolean, val: any}>({set: false, val: undefined})
  if (!stableInitialRef.current.set) stableInitialRef.current = {set: true, val: initialVal}
  const stableInitial = stableInitialRef.current.val

  const getSnapshot = () => {
    if (!syncCache.has(key)) syncCache.set(key, syncRead(storage, key, stableInitial, options))
    return syncCache.get(key)
  }

  const subscribe = useCallback((cb: () => void) => {
    if (!syncSubs.has(key)) syncSubs.set(key, new Set())
    syncSubs.get(key)!.add(cb)
    const onStorage = (e: {key: string | null}) => { if (e.key === key) syncNotify(key) }
    window.addEventListener('storage', onStorage)
    return () => {
      syncSubs.get(key)?.delete(cb)
      window.removeEventListener('storage', onStorage)
    }
  }, [key])

  const value = useSyncExternalStore(subscribe, getSnapshot, () => stableInitial)

  const setValue = useCallback((valueOrFn: any) => {
    const prev = syncCache.has(key) ? syncCache.get(key) : stableInitial
    const newValue = _.isFunction(valueOrFn) ? valueOrFn(prev) : valueOrFn
    let valueForStorage = newValue
    if (valueForStorage == undefined) valueForStorage = null
    const serNewVal = valueForStorage ? JSON.stringify(valueForStorage, options?.replacer) : JSON.stringify({_fixSerializeOfFalsies: valueForStorage}, options?.replacer)
    storage.setItem(key, serNewVal)
    syncNotify(key)
  }, [key, options, storage, stableInitial])

  return [value, setValue]
}

const LocalStorageCtx = createContext<any>(undefined)
export const LocalStorageWrapper = ({children}: {children: ReactNode}) => {
  const [globalState, setGlobalState] = useState({})
  const value = {globalState, setGlobalState}
  return <LocalStorageCtx.Provider {...{value, children}} />
}